import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  BookOpen,
  Calendar,
  GraduationCap,
  Award,
  FileText,
  Mic,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import logoWebp from "@/assets/aiswerve-192.webp";
import logo from "@/assets/aiswerve.png";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const learnLinks = [
  {
    name: "Programs",
    href: "/programs",
    desc: "AI training for corporates, startups, executives, and workforce.",
    icon: BookOpen,
  },
  {
    name: "Events",
    href: "/events",
    desc: "Upcoming tech events, seminars, workshops, and hackathons.",
    icon: Calendar,
  },
  {
    name: "Student Accelerator",
    href: "/student-talent-accelerator",
    desc: "Paid AI deployment opportunities for top university students.",
    icon: GraduationCap,
  },
  {
    name: "Certifications",
    href: "/certifications",
    desc: "Acquire professional vendor certifications in AI and cloud.",
    icon: Award,
  },
];

const resourcesLinks = [
  {
    name: "Articles & Guides",
    href: "/resources",
    desc: "Explore AI insights, toolkits, and educational resources.",
    icon: FileText,
  },
  {
    name: "Podcast",
    href: "/podcast",
    desc: "Listen to conversations on the future of AI talent in Africa.",
    icon: Mic,
  },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const isLearnActive = learnLinks.some((link) => isActive(link.href));
  const isResourcesActive = resourcesLinks.some((link) => isActive(link.href));

  const [isLearnExpanded, setIsLearnExpanded] = useState(isLearnActive);
  const [isResourcesExpanded, setIsResourcesExpanded] = useState(isResourcesActive);

  return (
    <nav className="bg-card shadow-soft sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center">
              <picture>
                <source srcSet={logoWebp} type="image/webp" />
                <img
                  src={logo}
                  alt="AISWERVEAFRICA Logo"
                  width={48}
                  height={48}
                  className="w-full h-full object-contain"
                  loading="eager"
                />
              </picture>
            </div>
            <span className="font-montserrat font-bold text-xl text-foreground">
              AISWERVEAFRICA
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center">
            <NavigationMenu>
              <NavigationMenuList className="space-x-8">
                {/* Home */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/"
                      className={cn(
                        "font-roboto text-sm font-medium transition-colors duration-200 hover:text-primary pb-1 block",
                        isActive("/") ? "text-primary border-b-2 border-primary" : "text-muted-foreground"
                      )}
                    >
                      Home
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* About */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/about"
                      className={cn(
                        "font-roboto text-sm font-medium transition-colors duration-200 hover:text-primary pb-1 block",
                        isActive("/about") ? "text-primary border-b-2 border-primary" : "text-muted-foreground"
                      )}
                    >
                      About
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Learn Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={cn(
                      "font-roboto text-sm font-medium transition-colors duration-200 bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent px-0 py-0 h-auto pb-1 block hover:text-primary focus:text-primary data-[state=open]:text-primary data-[active]:text-primary",
                      isLearnActive ? "text-primary border-b-2 border-primary" : "text-muted-foreground"
                    )}
                  >
                    Learn
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-card border border-border rounded-lg shadow-lg">
                      {learnLinks.map((link) => {
                        const Icon = link.icon;
                        return (
                          <li key={link.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={link.href}
                                className={cn(
                                  "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-muted/50 focus:bg-muted/50 group",
                                  isActive(link.href) ? "bg-primary/5" : ""
                                )}
                              >
                                <div className="flex items-center space-x-2">
                                  <Icon
                                    className={cn(
                                      "h-5 w-5 transition-colors duration-200",
                                      isActive(link.href) ? "text-primary" : "text-muted-foreground group-hover:text-primary"
                                    )}
                                  />
                                  <div
                                    className={cn(
                                      "text-sm font-semibold leading-none font-roboto transition-colors duration-200",
                                      isActive(link.href) ? "text-primary" : "text-foreground group-hover:text-primary"
                                    )}
                                  >
                                    {link.name}
                                  </div>
                                </div>
                                <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1">
                                  {link.desc}
                                </p>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        );
                      })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Impact */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/impact"
                      className={cn(
                        "font-roboto text-sm font-medium transition-colors duration-200 hover:text-primary pb-1 block",
                        isActive("/impact") ? "text-primary border-b-2 border-primary" : "text-muted-foreground"
                      )}
                    >
                      Impact
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Get Involved */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/get-involved"
                      className={cn(
                        "font-roboto text-sm font-medium transition-colors duration-200 hover:text-primary pb-1 block",
                        isActive("/get-involved") ? "text-primary border-b-2 border-primary" : "text-muted-foreground"
                      )}
                    >
                      Get Involved
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Resources Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={cn(
                      "font-roboto text-sm font-medium transition-colors duration-200 bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent px-0 py-0 h-auto pb-1 block hover:text-primary focus:text-primary data-[state=open]:text-primary data-[active]:text-primary",
                      isResourcesActive ? "text-primary border-b-2 border-primary" : "text-muted-foreground"
                    )}
                  >
                    Resources
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[300px] gap-3 p-4 bg-card border border-border rounded-lg shadow-lg">
                      {resourcesLinks.map((link) => {
                        const Icon = link.icon;
                        return (
                          <li key={link.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={link.href}
                                className={cn(
                                  "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-muted/50 focus:bg-muted/50 group",
                                  isActive(link.href) ? "bg-primary/5" : ""
                                )}
                              >
                                <div className="flex items-center space-x-2">
                                  <Icon
                                    className={cn(
                                      "h-5 w-5 transition-colors duration-200",
                                      isActive(link.href) ? "text-primary" : "text-muted-foreground group-hover:text-primary"
                                    )}
                                  />
                                  <div
                                    className={cn(
                                      "text-sm font-semibold leading-none font-roboto transition-colors duration-200",
                                      isActive(link.href) ? "text-primary" : "text-foreground group-hover:text-primary"
                                    )}
                                  >
                                    {link.name}
                                  </div>
                                </div>
                                <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1">
                                  {link.desc}
                                </p>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        );
                      })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Contact */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/contact"
                      className={cn(
                        "font-roboto text-sm font-medium transition-colors duration-200 hover:text-primary pb-1 block",
                        isActive("/contact") ? "text-primary border-b-2 border-primary" : "text-muted-foreground"
                      )}
                    >
                      Contact
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block ml-6">
            <Button variant="cta" size="sm" asChild>
              <Link to="/programs">Join a Bootcamp</Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? "Close menu" : "Open menu"}
              aria-expanded={isOpen}
              aria-controls="mobile-navigation"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden" id="mobile-navigation">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-card border-t border-border max-h-[calc(100vh-4rem)] overflow-y-auto">
              {/* Home */}
              <Link
                to="/"
                className={cn(
                  "block px-3 py-2 rounded-md text-base font-medium font-roboto transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isActive("/")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-primary hover:bg-muted"
                )}
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>

              {/* About */}
              <Link
                to="/about"
                className={cn(
                  "block px-3 py-2 rounded-md text-base font-medium font-roboto transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isActive("/about")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-primary hover:bg-muted"
                )}
                onClick={() => setIsOpen(false)}
              >
                About
              </Link>

              {/* Learn Accordion */}
              <div>
                <button
                  onClick={() => setIsLearnExpanded(!isLearnExpanded)}
                  aria-expanded={isLearnExpanded}
                  aria-controls="mobile-learn-menu"
                  className={cn(
                    "w-full flex justify-between items-center px-3 py-2 rounded-md text-base font-medium font-roboto transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isLearnActive
                      ? "bg-primary/5 text-primary"
                      : "text-muted-foreground hover:text-primary hover:bg-muted"
                  )}
                >
                  <span>Learn</span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform duration-200",
                      isLearnExpanded && "rotate-180"
                    )}
                  />
                </button>
                {isLearnExpanded && (
                  <div id="mobile-learn-menu" className="pl-6 mt-1 space-y-1 border-l-2 border-border/50 ml-3">
                    {learnLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.href}
                        className={cn(
                          "block px-3 py-2 rounded-md text-sm font-medium font-roboto transition-colors duration-200",
                          isActive(link.href)
                            ? "text-primary bg-primary/5"
                            : "text-muted-foreground hover:text-primary hover:bg-muted"
                        )}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-center space-x-2">
                          <link.icon className="h-4 w-4" />
                          <span>{link.name}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Impact */}
              <Link
                to="/impact"
                className={cn(
                  "block px-3 py-2 rounded-md text-base font-medium font-roboto transition-colors duration-200",
                  isActive("/impact")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-primary hover:bg-muted"
                )}
                onClick={() => setIsOpen(false)}
              >
                Impact
              </Link>

              {/* Get Involved */}
              <Link
                to="/get-involved"
                className={cn(
                  "block px-3 py-2 rounded-md text-base font-medium font-roboto transition-colors duration-200",
                  isActive("/get-involved")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-primary hover:bg-muted"
                )}
                onClick={() => setIsOpen(false)}
              >
                Get Involved
              </Link>

              {/* Resources Accordion */}
              <div>
                <button
                  onClick={() => setIsResourcesExpanded(!isResourcesExpanded)}
                  className={cn(
                    "w-full flex justify-between items-center px-3 py-2 rounded-md text-base font-medium font-roboto transition-colors duration-200",
                    isResourcesActive
                      ? "bg-primary/5 text-primary"
                      : "text-muted-foreground hover:text-primary hover:bg-muted"
                  )}
                >
                  <span>Resources</span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform duration-200",
                      isResourcesExpanded && "rotate-180"
                    )}
                  />
                </button>
                {isResourcesExpanded && (
                  <div className="pl-6 mt-1 space-y-1 border-l-2 border-border/50 ml-3">
                    {resourcesLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.href}
                        className={cn(
                          "block px-3 py-2 rounded-md text-sm font-medium font-roboto transition-colors duration-200",
                          isActive(link.href)
                            ? "text-primary bg-primary/5"
                            : "text-muted-foreground hover:text-primary hover:bg-muted"
                        )}
                        onClick={() => setIsOpen(false)}
                      >
                        <div className="flex items-center space-x-2">
                          <link.icon className="h-4 w-4" />
                          <span>{link.name}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Contact */}
              <Link
                to="/contact"
                className={cn(
                  "block px-3 py-2 rounded-md text-base font-medium font-roboto transition-colors duration-200",
                  isActive("/contact")
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-primary hover:bg-muted"
                )}
                onClick={() => setIsOpen(false)}
              >
                Contact
              </Link>

              <div className="pt-4 pb-2">
                <Button variant="cta" className="w-full" asChild>
                  <Link to="/programs" onClick={() => setIsOpen(false)}>
                    Join a Bootcamp
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
